import React, { useState } from 'react';
import { Shield, AlertTriangle, Lock, Eye, X, CheckCircle, AlertCircle } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { supabase } from '../lib/supabase';

interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ isOpen, onClose }) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { currentCompany, loadCompanies } = useTenant();

  const handleAcceptTerms = async () => {
    if (!acceptedTerms || !currentCompany) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('companies')
        .update({ 
          accept: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentCompany.id);

      if (error) {
        console.error('Error updating company acceptance:', error);
        alert('Error al actualizar la aceptación de términos. Por favor, inténtalo de nuevo.');
        return;
      }

      // Recargar la información de la empresa para actualizar el contexto
      await loadCompanies();
      
      alert('Términos aceptados correctamente. Ahora puedes crear líneas de negocio.');
      onClose();
    } catch (error) {
      console.error('Error accepting terms:', error);
      alert('Error al aceptar los términos. Por favor, inténtalo de nuevo.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-600 rounded-lg flex items-center justify-center mr-4">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Políticas de Privacidad y Uso</h2>
              <p className="text-sm text-gray-600">Información confidencial y términos de servicio</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            
            {/* Advertencia Principal */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-red-900 mb-2">
                    ⚠️ Información Confidencial
                  </h3>
                  <p className="text-red-800 mb-3">
                    <strong>Toda la información contenida en esta plataforma es CONFIDENCIAL</strong> y está protegida por las leyes de propiedad intelectual y privacidad de datos.
                  </p>
                  <div className="bg-red-100 border border-red-300 rounded p-3">
                    <p className="text-red-900 font-medium">
                      🚫 <strong>PROHIBIDO:</strong> Compartir, distribuir, copiar o hacer uso indebido de cualquier información, datos o funcionalidades de LicitaTrack.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Responsabilidad */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-6 w-6 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-orange-900 mb-2">
                    📋 Limitación de Responsabilidad
                  </h3>
                  <div className="text-orange-800 space-y-2">
                    <p>
                      <strong>LicitaTrack NO se hace responsable por:</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Mal uso de la plataforma por parte de los usuarios</li>
                      <li>Decisiones comerciales basadas en la información proporcionada</li>
                      <li>Pérdidas económicas derivadas del uso de la plataforma</li>
                      <li>Acciones de terceros que utilicen información obtenida de la plataforma</li>
                      <li>Interrupciones del servicio por causas externas</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Suspensión de Cuenta */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <Lock className="h-6 w-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                    🔒 Política de Suspensión
                  </h3>
                  <div className="text-yellow-800 space-y-3">
                    <p>
                      <strong>Su cuenta será SUSPENDIDA inmediatamente si detectamos:</strong>
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="bg-yellow-100 border border-yellow-300 rounded p-3">
                        <h4 className="font-semibold mb-2">🚫 Uso Prohibido:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Compartir credenciales de acceso</li>
                          <li>• Extraer datos masivamente</li>
                          <li>• Usar bots o automatización</li>
                          <li>• Revender información</li>
                        </ul>
                      </div>
                      <div className="bg-yellow-100 border border-yellow-300 rounded p-3">
                        <h4 className="font-semibold mb-2">⚠️ Violaciones:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Distribuir información confidencial</li>
                          <li>• Crear cuentas múltiples</li>
                          <li>• Actividades fraudulentas</li>
                          <li>• Violación de términos de uso</li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-red-100 border border-red-300 rounded p-3 mt-3">
                      <p className="text-red-900 font-semibold">
                        ⚠️ <strong>ADVERTENCIA:</strong> La suspensión es PERMANENTE y no hay proceso de apelación.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Privacidad de Datos */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Eye className="h-6 w-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    👁️ Privacidad y Protección de Datos
                  </h3>
                  <div className="text-blue-800 space-y-2">
                    <p>
                      <strong>Protegemos tu información personal:</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Encriptación de datos en tránsito y reposo</li>
                      <li>Cumplimiento con la Ley de Protección de Datos Personales</li>
                      <li>No compartimos información con terceros sin consentimiento</li>
                      <li>Acceso restringido solo a personal autorizado</li>
                      <li>Monitoreo continuo de seguridad</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Términos de Uso */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-green-900 mb-2">
                    ✅ Términos de Uso Aceptados
                  </h3>
                  <div className="text-green-800 space-y-2">
                    <p>
                      Al usar LicitaTrack, aceptas automáticamente:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Usar la plataforma únicamente para fines comerciales legítimos</li>
                      <li>Mantener la confidencialidad de la información</li>
                      <li>No realizar actividades que puedan dañar la plataforma</li>
                      <li>Reportar cualquier uso indebido que observes</li>
                      <li>Cumplir con todas las leyes y regulaciones aplicables</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Información de Contacto */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                📞 Contacto y Soporte
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Para Reportar Violaciones:</h4>
                  <p className="text-gray-700 text-sm">
                    <strong>Email:</strong> seguridad@licitatrak.cl<br />
                    <strong>Asunto:</strong> Reporte de Violación de Términos
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Soporte General:</h4>
                  <p className="text-gray-700 text-sm">
                    <strong>Email:</strong> contacto@licitatrak.cl<br />
                    <strong>Horario:</strong> Lunes a Viernes, 9:00 - 18:00
                  </p>
                </div>
              </div>
            </div>

            {/* Checkbox de Aceptación */}
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm text-gray-700">
                  <strong>He leído y acepto</strong> todas las políticas de privacidad, términos de uso y limitaciones de responsabilidad descritas anteriormente. 
                  Entiendo que el mal uso de la plataforma resultará en la suspensión permanente de mi cuenta.
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-600">
              <Shield className="h-4 w-4 mr-2 text-gray-500" />
              <span>Última actualización: {new Date().toLocaleDateString('es-CL')}</span>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={handleAcceptTerms}
                disabled={!acceptedTerms || isUpdating}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  acceptedTerms && !isUpdating
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isUpdating ? 'Actualizando...' : 'Aceptar Términos'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
